import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import api from '../utils/api';
import PipelineStatus from '../components/PipelineStatus';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function InvestorDashboard() {
    const [myPurchases, setMyPurchases] = useState([]);
    const [myRetirements, setMyRetirements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
    const navigate = useNavigate();

    const [downloading, setDownloading] = useState(null);

    // Modal State
    const [retireModal, setRetireModal] = useState(null);
    const [retireNotes, setRetireNotes] = useState('');

    // Plants & Filtering
    const [plants, setPlants] = useState([]);
    const [expandedAssetId, setExpandedAssetId] = useState(null);

    useEffect(() => {
        document.title = "Investor Portfolio | Silicon Himalayas";
        fetchData();
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, []);

    const fetchData = async () => {
        try {
            const [resPurchased, resRetired, resPlants] = await Promise.all([
                api.get('/api/investor/attestations?status=purchased'),
                api.get('/api/investor/my-retirements'),
                api.get('/api/plants')
            ]);
            setMyPurchases(resPurchased.data || []);
            setMyRetirements(resRetired.data || []);
            setPlants(resPlants.data || []);
            setLoading(false);
        } catch (err) {
            console.error("Fetch failed", err);
            setLoading(false);
        }
    };

    const totalMwhRetired = myRetirements.reduce((sum, a) => 
        sum + parseFloat(a.mwh || 0), 0
    );
    const totalMwhPurchased = myPurchases.reduce((sum, a) => 
        sum + parseFloat(a.mwh || 0), 0
    );

    const chartData = {
        labels: ['MWh Retired', 'MWh Purchased (Pending)'],
        datasets: [{
            data: [
                totalMwhRetired || 0.1, 
                totalMwhPurchased || 0.1
            ],
            backgroundColor: [
                'rgba(16, 185, 129, 0.8)',
                'rgba(99, 102, 241, 0.5)'
            ],
            borderColor: 'rgba(255, 255, 255, 0.05)',
            borderWidth: 2,
            hoverOffset: 10
        }]
    };

    const handleRetire = async () => {
        if (!retireModal) return;
        setSubmitting(true);
        try {
            await api.post(`/api/investor/retire/${retireModal.id}`, { notes: retireNotes });
            setMessage('✓ Asset successfully retired. ESG claim committed to the green ledger.');
            setRetireModal(null);
            setRetireNotes('');
            fetchData();
            setTimeout(() => setMessage(''), 6000);
        } catch (err) {
            setError('Retirement failed');
            setTimeout(() => setError(''), 6000);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadPdf = async (id) => {
        setDownloading(id);
        try {
            const response = await api.get(`/api/certificates/${id}/pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Green_Energy_Certificate_${id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Failed to download PDF", error);
            setError("Failed to download or generate PDF.");
            setTimeout(() => setError(''), 5000);
        } finally {
            setDownloading(null);
        }
    };

    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
    };

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center bg-[#0a0c12] text-emerald-500 font-black tracking-widest animate-pulse">
                SYNCHRONIZING PORTFOLIO...
            </div>
        );
    }

    return (
        <div className={`min-h-screen transition-colors duration-500 ${theme === 'dark' ? 'bg-[#0a0c12] text-slate-200' : 'bg-slate-50 text-slate-600'} p-4 sm:p-8 font-sans`}>
            {/* Header */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10 max-w-7xl mx-auto">
                <Link to="/">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                            <svg className="w-8 h-8 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 20L8 8L13 16" />
                                <path d="M8 20L15 6L22 20" />
                                <path d="M8 8L15 6" strokeDasharray="1.5 1.5" />
                                <circle cx="15" cy="6" r="1.5" fill="currentColor" />
                            </svg>
                        </div>
                        <h1 className={`text-3xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Investor <span className="text-emerald-500 italic">Portfolio</span></h1>
                    </div>
                    <p className="text-slate-500 text-xs mt-1 uppercase tracking-[0.3em] font-black ml-13">Sustainability Asset Management</p>
                </Link>
                <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
                    <Link 
                        to="/dashboard/investor/marketplace" 
                        className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-2xl font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/10 flex-1 sm:flex-initial text-center justify-center"
                    >
                        🛒 Go to Marketplace
                    </Link>
                    <button onClick={toggleTheme} className={`p-3 rounded-2xl border transition-all ${theme === 'dark' ? 'bg-white/5 border-white/10 text-yellow-550' : 'bg-white border-slate-200 text-slate-800 shadow-sm'}`}>
                        {theme === 'dark' ? '☀️' : '🌙'}
                    </button>
                    <button onClick={() => { localStorage.clear(); navigate('/login'); }} className={`px-5 py-2.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-2xl transition font-bold text-xs uppercase flex-1 sm:flex-initial text-center justify-center`}>Logout</button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto space-y-10">
                {(message || error) && (
                    <div className={`${error ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'} p-4 rounded-3xl border flex items-center gap-3 animate-slide-in`}>
                        <div className={`w-2 h-2 rounded-full animate-pulse ${error ? 'bg-rose-500' : 'bg-emerald-500'}`}></div>
                        <span className="font-bold text-sm tracking-wide uppercase">{message || error}</span>
                    </div>
                )}

                {/* Marketplace Call-To-Action Banner */}
                <section className={`p-8 rounded-[2.5rem] border flex flex-col md:flex-row justify-between items-center gap-6 transition-all duration-300 ${
                    theme === 'dark' 
                        ? 'bg-gradient-to-r from-emerald-950/20 to-cyan-950/20 border-white/5 shadow-2xl' 
                        : 'bg-gradient-to-r from-emerald-50/50 to-cyan-50/50 border-slate-200 shadow-xl'
                }`}>
                    <div>
                        <h2 className={`text-xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Emissions Offsetting & Green Certificates</h2>
                        <p className="text-slate-550 text-xs mt-2 leading-relaxed max-w-xl">
                            Browse and purchase active renewable energy certificates (I-RECs) from Nepalese solar and run-of-river hydro plants. Retire your holdings on the blockchain to generate verified Scope 2 offsets.
                        </p>
                    </div>
                    <Link 
                        to="/dashboard/investor/marketplace" 
                        className="px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black rounded-2xl text-[10px] uppercase tracking-widest transition-all shadow-lg hover:shadow-emerald-500/20 flex items-center gap-2 whitespace-nowrap"
                    >
                        🛒 Explore Marketplace →
                    </Link>
                </section>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 animate-fade-in">
                    <div className="lg:col-span-2 space-y-10">
                        {/* Purchased Certificates (Pending Retirement) */}
                        <section className={`${theme === 'dark' ? 'glass border-white/5 shadow-2xl' : 'bg-white border-slate-200 shadow-xl'} rounded-[2.5rem] border overflow-hidden`}>
                            <div className={`p-8 ${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-105'} border-b flex justify-between items-center`}>
                                <div className="flex items-center gap-4">
                                    <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Purchased Certificates (Pending Retirement)</h2>
                                    <span className="bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Active Assets</span>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className={`text-[10px] font-black uppercase tracking-widest ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                                        <tr>
                                            <th className="p-5 pl-6">Certificate ID</th>
                                            <th className="p-5">Vintage</th>
                                            <th className="p-5">Volume</th>
                                            <th className="p-5">Source Project</th>
                                            <th className="p-5">Technology</th>
                                            <th className="p-5">I-REC Status</th>
                                            <th className="p-5 text-right pr-6">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${theme === 'dark' ? 'divide-white/5' : 'divide-slate-100'}`}>
                                        {myPurchases.map(a => {
                                            const matchingPlant = plants.find(p => String(p.id) === String(a.plantId || a.projectId));
                                            return (
                                                <tr key={a.id} className="group hover:bg-emerald-500/5 transition-colors">
                                                    <td className="p-5 pl-6">
                                                        <p className="font-mono text-[10px] opacity-50 mb-1">
                                                            {a.id.substring(0, 14)}...
                                                        </p>
                                                        <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest">
                                                            SHL-{new Date(a.timestamp).getFullYear()}
                                                        </p>
                                                    </td>
                                                    <td className="p-5">
                                                        <span className="text-xs font-bold">
                                                            {a.vintagePeriod || new Date(a.timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                                        </span>
                                                    </td>
                                                    <td className="p-5">
                                                        <p className="font-bold text-lg tracking-tight text-emerald-400">
                                                            {a.mwh} <span className="text-xs text-slate-500 ml-1 font-normal">MWh</span>
                                                        </p>
                                                    </td>
                                                    <td className="p-5">
                                                        {(a.plantId || a.projectId) ? (
                                                            <Link to={`/plant/${a.plantId || a.projectId}`} className="text-cyan-400 hover:text-cyan-300 text-xs font-bold hover:underline">
                                                                {a.plantName || (matchingPlant?.plant_name) || a.projectId}
                                                            </Link>
                                                        ) : (
                                                            <span className="text-slate-500 text-xs">—</span>
                                                        )}
                                                    </td>
                                                    <td className="p-5">
                                                        <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-lg block w-fit ${
                                                            (a.fuelType || 'Solar').toLowerCase() === 'solar' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-blue-500/10 text-blue-400'
                                                        }`}>
                                                            {a.fuelType || 'Solar'} · {a.technologyType || 'PV'}
                                                        </span>
                                                    </td>
                                                    <td className="p-5">
                                                        {a.evidentSerial ? (
                                                            <div>
                                                                <span className="text-[9px] font-bold text-emerald-400 block">✓ Evident Issued</span>
                                                                <span className="font-mono text-[8px] text-slate-500">{a.evidentSerial}</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-[9px] text-yellow-500 font-bold uppercase tracking-wide">⏳ Pending GCC</span>
                                                        )}
                                                    </td>
                                                    <td className="p-5 text-right pr-6">
                                                        <button 
                                                            onClick={() => setRetireModal(a)} 
                                                            className="px-5 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg hover:shadow-cyan-500/20"
                                                        >
                                                            Retire
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {myPurchases.length === 0 && (
                                    <div className="p-20 text-center text-slate-500 font-bold uppercase tracking-widest text-xs italic">
                                        No active holdings. Purchase certificates from the marketplace to see them in your portfolio.
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* My Retirements */}
                        <section className={`${theme === 'dark' ? 'glass border-white/5 shadow-2xl' : 'bg-white border-slate-200 shadow-xl'} rounded-[2.5rem] border overflow-hidden`}>
                            <div className={`p-8 ${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-100'} border-b flex justify-between items-center`}>
                                <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Retired Certificates · ESG Claims</h2>
                                <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Scope 2 Verified</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className={`text-[10px] font-black uppercase tracking-widest ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                                        <tr>
                                            <th className="p-5 pl-6">Certificate ID</th>
                                            <th className="p-5">Vintage</th>
                                            <th className="p-5">Volume</th>
                                            <th className="p-5">Source Project</th>
                                            <th className="p-5">ESG Purpose</th>
                                            <th className="p-5">Evident Status</th>
                                            <th className="p-5 text-right pr-6">Certificate</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${theme === 'dark' ? 'divide-white/5' : 'divide-slate-100'}`}>
                                        {myRetirements.map(a => {
                                            const matchingPlant = plants.find(p => String(p.id) === String(a.plantId || a.projectId));
                                            return (
                                                <React.Fragment key={a.id}>
                                                    <tr className="hover:bg-white/5 transition-colors cursor-pointer" onClick={() => setExpandedAssetId(expandedAssetId === a.id ? null : a.id)}>
                                                        <td className="p-5 pl-6">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[10px] text-slate-500">{expandedAssetId === a.id ? '▼' : '▶'}</span>
                                                                <div>
                                                                    <p className="font-mono text-[10px] opacity-50 mb-1">
                                                                        {a.id.substring(0, 14)}...
                                                                    </p>
                                                                    <p className="text-[9px] text-emerald-500 font-bold uppercase tracking-widest">RETIRED</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="p-5">
                                                            <span className="text-xs font-bold">
                                                                {a.vintagePeriod || new Date(a.retirementDate || a.timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                                            </span>
                                                        </td>
                                                        <td className="p-5">
                                                            <p className="font-bold text-emerald-400">{a.mwh} MWh</p>
                                                            <p className="text-[9px] text-slate-600 font-bold">= {a.mwh} I-REC(E)</p>
                                                        </td>
                                                        <td className="p-5 text-xs font-bold">
                                                            {(a.plantId || a.projectId) ? (
                                                                <Link to={`/plant/${a.plantId || a.projectId}`} className="text-cyan-400 hover:text-cyan-300 hover:underline block" onClick={e => e.stopPropagation()}>
                                                                    {a.plantName || (matchingPlant?.plant_name) || a.projectId}
                                                                </Link>
                                                            ) : (
                                                                <span className="text-slate-500 text-xs">—</span>
                                                            )}
                                                        </td>
                                                        <td className="p-5 text-xs text-slate-400 italic">"{a.retirementNotes || 'No notes provided'}"</td>
                                                        <td className="p-5">
                                                            {a.evidentSerial ? (
                                                                <div>
                                                                    <span className="text-[9px] text-emerald-400 font-bold block">✓ Evidenced</span>
                                                                    <span className="font-mono text-[8px] text-slate-500 block mt-0.5">{a.evidentSerial}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-[9px] text-yellow-500">Pending Evident</span>
                                                            )}
                                                        </td>
                                                        <td className="p-5 text-right pr-6" onClick={e => e.stopPropagation()}>
                                                            <button 
                                                                onClick={() => handleDownloadPdf(a.id)} 
                                                                disabled={downloading === a.id}
                                                                className={`px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500 text-emerald-500 hover:text-white border border-emerald-500/30 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${downloading === a.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                {downloading === a.id ? 'Generating...' : 'Download PDF'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {expandedAssetId === a.id && (
                                                        <tr>
                                                            <td colSpan="7" className="p-5 bg-white/5 border-b border-white/5">
                                                                <div className="space-y-4">
                                                                    <div className="flex justify-between items-center px-2">
                                                                        <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">Onboarding & Issuance Pipeline</h4>
                                                                        {a.evidentSerial && (
                                                                            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20">Evident Serial: {a.evidentSerial}</span>
                                                                        )}
                                                                    </div>
                                                                    <PipelineStatus 
                                                                        attestation={a} 
                                                                        plant={matchingPlant} 
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {myRetirements.length === 0 && <div className="p-20 text-center text-slate-500 font-bold uppercase tracking-widest text-xs italic">No retired certificates yet. Purchase certificates from the marketplace, then retire them to generate your Scope 2 ESG claim.</div>}
                            </div>
                        </section>
                    </div>

                    {/* Sidebar: Analytics */}
                    <aside className="space-y-10 col-span-1">
                        <section className={`${theme === 'dark' ? 'glass border-white/5 shadow-2xl' : 'bg-white border-slate-200 shadow-xl'} rounded-[2.5rem] border p-8 transition-all`}>
                            <h3 className={`text-xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Portfolio Analytics</h3>
                            <div className="h-48 mb-6">
                                <Doughnut 
                                    data={chartData}
                                    options={{
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: {
                                                position: 'bottom',
                                                labels: {
                                                    color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                                    font: { size: 10, weight: 'bold' },
                                                    usePointStyle: true,
                                                    padding: 20
                                                }
                                            }
                                        },
                                        cutout: '70%'
                                    }}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-emerald-500/5 p-4 rounded-2xl border border-emerald-500/10 text-center">
                                    <p className="text-[9px] font-black uppercase text-emerald-500/50 mb-1">MWh Retired</p>
                                    <p className="text-xl font-black text-emerald-500">
                                        {totalMwhRetired.toFixed(1)}
                                    </p>
                                </div>
                                <div className="bg-indigo-500/5 p-4 rounded-2xl border border-indigo-500/10 text-center">
                                    <p className="text-[9px] font-black uppercase text-indigo-400/50 mb-1">MWh Purchased</p>
                                    <p className="text-xl font-black text-indigo-400">
                                        {totalMwhPurchased.toFixed(1)}
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section className={`rounded-[2.5rem] border p-8 transition-all ${theme === 'dark' ? 'glass border-white/5 shadow-2xl' : 'bg-white border-slate-200 shadow-xl'}`}>
                            <h3 className={`text-lg font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                                How I-RECs Work
                            </h3>
                            <div className="space-y-4">
                                {[
                                    { 
                                        step: '1', 
                                        title: 'Browse', 
                                        desc: 'Find certificates from verified Nepali solar and hydro projects.'
                                    },
                                    { 
                                        step: '2', 
                                        title: 'Purchase', 
                                        desc: 'Acquire certificates. 1 I-REC = 1 MWh of renewable generation.'
                                    },
                                    { 
                                        step: '3', 
                                        title: 'Retire', 
                                        desc: 'Retire certificates in your portfolio to generate verified Scope 2 ESG claims.'
                                    }
                                ].map((s, i) => (
                                    <div key={i} className="flex gap-4 items-start">
                                        <div className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 font-bold flex items-center justify-center text-xs shrink-0">{s.step}</div>
                                        <div>
                                            <h4 className={`font-bold text-xs ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{s.title}</h4>
                                            <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">{s.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </aside>
                </div>
            </main>

            {/* Retire Modal */}
            {retireModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-fade-in shadow-2xl">
                    <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={() => !submitting && setRetireModal(null)}></div>
                    <div className={`${theme === 'dark' ? 'glass border-white/10' : 'bg-white border-slate-200'} w-full max-w-lg rounded-[2.5rem] border relative z-10 shadow-2xl animate-scale-in`}>
                        <div className={`p-8 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-105'} flex justify-between items-center`}>
                            <div>
                                <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Offset Retirement</h3>
                                <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest font-bold">Asserting Sustainability Commitment</p>
                            </div>
                            <button onClick={() => setRetireModal(null)} className="p-2 hover:bg-white/10 rounded-full transition text-slate-500"><svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="bg-emerald-500/5 p-4 rounded-2xl border border-emerald-500/20">
                                <p className="text-[10px] text-emerald-500 font-black uppercase tracking-widest mb-1">Target Asset</p>
                                <p className={`font-bold text-lg ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{retireModal.mwh} MWh</p>
                                <p className="text-[9px] font-mono opacity-50 truncate">{retireModal.id}</p>
                            </div>
                            <div>
                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-500 mb-3 ml-1">Offset Narration</label>
                                <textarea 
                                    value={retireNotes}
                                    onChange={e => setRetireNotes(e.target.value)}
                                    placeholder="Briefly describe the purpose of this retirement (e.g. Scope 2 ESG Report)..."
                                    className={`w-full ${theme === 'dark' ? 'bg-[#080a0e] border-white/5 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-800'} border rounded-2xl p-4 text-xs focus:outline-none focus:border-emerald-500/50 h-32 resize-none transition-all shadow-inner`}
                                />
                            </div>
                            <button onClick={handleRetire} disabled={submitting} className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg shadow-emerald-500/20 transition-all active:scale-95">
                                {submitting ? 'EXECUTING RETIREMENT...' : 'Finalize Ledger Retirement'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
